import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";
import useIdentity from "@/features/ui/hooks/use-identity";
import { ProformaInvoiceRow } from "@/features/invoices/proforma-invoices/proforma-invoice-row.type";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import useListProformaInvoices, {
  ListProformaInvoicesPathParameter,
  ListProformaInvoicesRequest,
} from "@/features/invoices/proforma-invoices/hooks/use-list-proforma-invoices";
import { ProformaInvoice } from "@/features/invoices/proforma-invoices/proforma-invoice.type";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import AppGrid, {
  AppGridColumn,
  AppGridField,
  AppGridMeta,
} from "@/features/order-sheet-sets/app-grid";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { GridRow } from "@/features/order-sheet-sets/helpers/app-grid-row-helper";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import useAppGrid from "@/features/invoices/hooks/use-app-grid";
import { proformaInvoiceRowMapper } from "@/features/invoices/utils/proforma-invoice-row-mapper";
import useInvoiceGrid from "@/features/invoices/hooks/use-invoice-grid";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import IsolatedPanelSkeleton from "@/features/invoices/isolated-panel-skeleton";
import { LightPayment } from "@/features/invoices/payment/payment.type";
import {
  getReceiptUploadMenu,
  RECEIPT_UPLOAD_MENU_KEYS,
} from "@/features/invoices/utils/proforma-receipt-upload-menu";

interface UseIsolatedOrderSheetSetGridProps {
  proformaInvoiceIds?: number[];
  title?: string;
  isCompact?: boolean;
  showNoRows?: boolean;
  setIsAddBankRemittanceReceiptOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setPayment?: React.Dispatch<React.SetStateAction<LightPayment | undefined>>;
  onUploadReceipt?: (proformaInvoiceId: number, payment: LightPayment) => void;
  runMutation?: boolean | number;
  onEdit?: (proformaInvoiceId: number) => void;
  onListData?: (proformaInvoices: ProformaInvoice[]) => void;
}

const rowMapper = proformaInvoiceRowMapper.copy();

export default function IsolatedProformaInvoicePanel({
  proformaInvoiceIds,
  title = "related_proforma_invoices",
  isCompact = false,
  showNoRows = true,
  setIsAddBankRemittanceReceiptOpen = undefined,
  setPayment = undefined,
  onUploadReceipt = undefined,
  runMutation = false,
  onEdit = undefined,
  onListData = undefined,
}: UseIsolatedOrderSheetSetGridProps) {
  const identity = useIdentity();
  const { tTitle, tCurrencyString } = useI18nHelper();
  const { navigate } = useRouterHelper();
  const { error: showError, add: addAppToast } = useAppToasts();
  const [pendingBoutiquePayment, setPendingBoutiquePayment] = useState<LightPayment | undefined>(undefined);
  const [pendingBoutiquePaymentRowId, setPendingBoutiquePaymentRowId] = useState<number | undefined>(undefined);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const key = useMemo(():
    | {
        parameter: ListProformaInvoicesPathParameter;
        request: ListProformaInvoicesRequest;
      }
    | undefined => {
    if (
      identity?.company?.type &&
      proformaInvoiceIds &&
      proformaInvoiceIds.length > 0
    ) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request: {
          id__in: proformaInvoiceIds,
          pageNumber: 1,
          pageSize: proformaInvoiceIds.length,
        },
      };
    }
    return undefined;
  }, [identity, proformaInvoiceIds]);

  const {
    data: listProformaInvoicesData,
    error: listProformaInvoicesError,
    mutate: mutateListProformaInvoices,
  } = useListProformaInvoices(key?.parameter, key?.request);

  useEffect(() => {
    if (runMutation) {
      mutateListProformaInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runMutation]);

  const appGrid = useAppGrid();


  const [rows, setRows] = useState<ProformaInvoiceRow[] | undefined>(undefined);

  useEffect(() => {
    if (listProformaInvoicesData) {
      onListData?.(listProformaInvoicesData.proformaInvoices);
      setRows((prev) => {
        return rowMapper.toRows(
          listProformaInvoicesData.proformaInvoices.map((pInvoice) => {
            return {
              ...pInvoice,
              isRead: false,
            };
          }),
          prev
        );
      });
    }
  }, [listProformaInvoicesData, onListData]);

  const invoiceGrid = useInvoiceGrid({
    rows,
    mutateRows: mutateListProformaInvoices,
  });

  const metas = useMemo((): AppGridMeta[] => {
    let pairs: { column: AppGridColumn; field: AppGridField }[] = [];

    if (identity?.company?.type === "BUYER") {
      pairs = [
        invoiceGrid.pairs.issuedOn,
        // invoiceGrid.pairs.issuedBy,
        // invoiceGrid.pairs.issuedThrough,
        // invoiceGrid.pairs.orderedBy,
        invoiceGrid.pairs.title,
        invoiceGrid.pairs.totalRequiredAmount,
        invoiceGrid.pairs.remittanceStatus,
        // invoiceGrid.pairs.paidBy,
      ];
    } else if (identity?.company?.type === "AGENCY") {
      pairs = [
        invoiceGrid.pairs.issuedOn,
        // invoiceGrid.pairs.issuedBy,
        // invoiceGrid.pairs.issuedThrough,
        // invoiceGrid.pairs.orderedBy,
        invoiceGrid.pairs.title,
        invoiceGrid.pairs.totalRequiredAmount,
        // invoiceGrid.pairs.buyerRemittedAmount,
        // invoiceGrid.pairs.agencyRemittedAmount,
        invoiceGrid.pairs.paymentRequiredAmount,
        invoiceGrid.pairs.remittanceStatus,
        // invoiceGrid.pairs.paidBy,
      ];
    } else if (identity?.company?.type === "BOUTIQUE") {
      pairs = [
        invoiceGrid.pairs.issuedOn,
        invoiceGrid.pairs.orderedBy,
        invoiceGrid.pairs.title,
        invoiceGrid.pairs.totalRequiredAmount,
        invoiceGrid.pairs.remittanceStatus,
        invoiceGrid.pairs.paidBy,
      ];
    }

    return [
      {
        rowType: "ProformaInvoice",
        gap: "8px",
        columns: pairs.map((pair) => pair.column),
        fields: pairs.map((pair) => pair.field),
        indicator: (row: GridRow) => {
          return (
            <GridRowIndicator
              type={"VIEW"}
              isCollapsed={row.isCollapsed}
              isHovered={row.isHovered}
            />
          );
        },
        onClick: (row: GridRow) => {
          if (onEdit) {
            onEdit(row.id);
          } else {
            navigate(`/proforma-invoices/${row.id}`);
          }
        },
        // 지불 주체(paidBy)에 따라 띄울 항목이 다르다 — 해당 없는 항목은 undefined 로 숨긴다
        actions: RECEIPT_UPLOAD_MENU_KEYS.map((key) => (_row: GridRow) => {
          const item = getReceiptUploadMenu(_row as ProformaInvoiceRow).find(
            (candidate) => candidate.key === key
          );
          if (!item) {
            return undefined;
          }
          return {
            name: item.name,
            isDisabled: item.isDisabled,
            isLoading: false,
            onClick: () => {
              if (!item.payment) {
                addAppToast({ status: "error", message: item.notFoundMessage });
              } else if (item.confirmsPreRemittance) {
                setPendingBoutiquePayment(item.payment);
                setPendingBoutiquePaymentRowId(_row.id);
              } else if (onUploadReceipt) {
                onUploadReceipt(_row.id, item.payment);
              } else if (setPayment && setIsAddBankRemittanceReceiptOpen) {
                setPayment(item.payment);
                setIsAddBankRemittanceReceiptOpen(true);
              }
            },
          };
        }),
      },
    ];
  }, [addAppToast, identity?.company?.type, invoiceGrid.pairs.paymentRequiredAmount, invoiceGrid.pairs.issuedOn, invoiceGrid.pairs.orderedBy, invoiceGrid.pairs.paidBy, invoiceGrid.pairs.remittanceStatus, invoiceGrid.pairs.title, invoiceGrid.pairs.totalRequiredAmount, navigate, onEdit, onUploadReceipt, setIsAddBankRemittanceReceiptOpen, setPayment, setPendingBoutiquePayment]);

  if (key === undefined) {
    return null;
  } else if (rows === undefined) {
    return <IsolatedPanelSkeleton />;
  }

  return (
    <>
      <AppPanel title={tTitle(title)} variant={"ghost"} isCompact={isCompact}>
        <AppGrid isAnchored={false} rows={rows} metas={metas} showNoRows={showNoRows}/>
      </AppPanel>
      <AlertDialog
        isOpen={!!pendingBoutiquePayment}
        leastDestructiveRef={cancelRef}
        onClose={() => setPendingBoutiquePayment(undefined)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              선송금 처리 안내
            </AlertDialogHeader>
            <AlertDialogBody>
              바이어의 송금 영수증이 아직 업로드되지 않았습니다. 지금 부티크 해외 송금 영수증을 먼저 업로드하면 선송금 처리됩니다. 계속하시겠습니까?
            </AlertDialogBody>
            <AlertDialogFooter gap={"8px"}>
              <Button ref={cancelRef} onClick={() => setPendingBoutiquePayment(undefined)}>
                취소
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  if (pendingBoutiquePayment) {
                    if (onUploadReceipt && pendingBoutiquePaymentRowId != null) {
                      onUploadReceipt(pendingBoutiquePaymentRowId, pendingBoutiquePayment);
                    } else if (setPayment && setIsAddBankRemittanceReceiptOpen) {
                      setPayment(pendingBoutiquePayment);
                      setIsAddBankRemittanceReceiptOpen(true);
                    }
                  }
                  setPendingBoutiquePayment(undefined);
                  setPendingBoutiquePaymentRowId(undefined);
                }}
              >
                확인
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
